import React, { useState, useEffect, useRef } from "react";
import Context from "../../Context";
import { Header } from "../../components/modules";
import { IoReceiptOutline } from "react-icons/io5";
import { BiLogIn } from "react-icons/bi";
import { FiSearch, FiRefreshCw } from "react-icons/fi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import constant, { statuses } from "../../constants";
import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";

const normalizeOrder = (doc) => {
  const d = doc.data();
  return { ...d, id: doc.id, slips: d.slips?.at(-1) ?? null };
};

export const PromoCodeOrders = () => {
  const { configs, db } = Context();

  const [rawOrders, setRawOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roundDate, setRoundDate] = useState(configs.roundDate);
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("shortId");
  const [status, setStatus] = useState("");
  const unsubRef = useRef(null);

  useEffect(() => {
    getOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawOrders, status, searchValue, searchType, roundDate]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, []);

  const getOrders = () => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    setLoading(true);

    const q = query(
      collection(db, "Orders"),
      where("isUsePromoCode", "==", true)
    );

    unsubRef.current = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(normalizeOrder);
        setRawOrders(docs);
        setLoading(false);
      },
      (err) => {
        console.error("promocode orders onSnapshot error:", err);
        setLoading(false);
      }
    );
  };

  const applyFilters = () => {
    let result = [...rawOrders];

    if (roundDate) {
      result = result.filter((o) => o.roundDate === roundDate);
    }

    if (status) {
      result = result.filter((o) => o.status === status);
    }

    if (searchValue.trim()) {
      const searchLower = searchValue.trim().toLowerCase();
      result = result.filter((o) => {
        if (searchType === "shortId") {
          return o.shortId?.toLowerCase().includes(searchLower);
        }
        if (searchType === "phone") {
          return o.phone?.includes(searchLower);
        }
        if (searchType === "userId") {
          return o.userId?.toLowerCase().includes(searchLower);
        }
        if (searchType === "firstName") {
          return o.firstName?.toLowerCase().includes(searchLower);
        }
        if (searchType === "lastName") {
          return o.lastName?.toLowerCase().includes(searchLower);
        }
        if (searchType === "promoCode") {
          return o.promoCode?.toLowerCase().includes(searchLower);
        }
        return false;
      });
    }

    // Sort by createdAt desc
    result.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    setOrders(result);
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-10 min-w-0">
      <Header
        header="Promo Code Orders"
        subHeader="คำสั่งซื้อสลากทั้งหมดที่ใช้รหัสโปรโมชั่น"
        icon={<IoReceiptOutline className="text-blue" />}
      />

      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 p-4 flex flex-col gap-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters();
          }}
          className="flex flex-wrap items-end gap-3 shrink-0"
        >
          <div className="">
            <label className="text-xs font-bold text-gray-700">งวดสลาก</label>
            <div className="relative">
              <select
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all cursor-pointer px-3"
                onChange={(e) => setRoundDate(e.target.value)}
                value={roundDate || ""}
              >
                {configs.roundDates.map((date, index) => (
                  <option key={index} value={date}>
                    {date}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="">
            <label className="text-xs font-bold text-gray-700">สถานะ</label>
            <div className="relative">
              <select
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all cursor-pointer px-3"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">ทั้งหมด</option>
                <option value={constant.statuses.APPROVE}>
                  {constant.statusesTranslate.approved}
                </option>
                <option value={constant.statuses.PAID}>
                  {constant.statusesTranslate.paid}
                </option>
                <option value={constant.statuses.PENDING}>
                  {constant.statusesTranslate.pending}
                </option>
                <option value={constant.statuses.CANCEL}>
                  {constant.statusesTranslate.canceled}
                </option>
              </select>
            </div>
          </div>

          <div className="">
            <label className="text-xs font-bold text-gray-700">ค้นหาตาม</label>
            <div className="relative">
              <select
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all cursor-pointer px-3"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
              >
                <option value="shortId">รหัสคำสั่งซื้อ</option>
                <option value="promoCode">รหัสโปรโมชั่น</option>
                <option value="phone">เบอร์โทรศัพท์</option>
                <option value="userId">รหัสผู้ใช้</option>
                <option value="firstName">ชื่อ</option>
                <option value="lastName">นามสกุล</option>
              </select>
            </div>
          </div>

          <div className="">
            <label className="text-xs font-bold text-gray-700">คำค้นหา</label>
            <div className="relative flex items-center">
              <input
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all px-3 placeholder:text-gray-400"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="พิมพ์เพื่อค้นหา..."
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={loading}
              className="h-10 px-4 flex items-center justify-center gap-1.5 bg-blue hover:bg-blue/90 text-white text-sm font-bold rounded-md shadow-xs transition-all duration-150 disabled:opacity-50 cursor-pointer"
            >
              <FiSearch className="w-4 h-4" />
              <span>ค้นหา</span>
            </button>
            <button
              type="button"
              disabled={loading}
              className="h-10 px-4 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-md border border-gray-300 shadow-xs transition-all cursor-pointer disabled:opacity-50"
              onClick={() => {
                setSearchValue("");
                setStatus("");
              }}
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span>รีเซ็ต</span>
            </button>
          </div>
        </form>
      </div>

      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 overflow-hidden">
        <div className="flex items-center p-4 border-b border-gray-200 bg-gray-50/60 justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-800">คำสั่งซื้อที่ใช้รหัสโปรโมชั่น</h2>
            <span className="px-2.5 py-0.5 text-xs font-bold bg-gray-200 text-gray-700 rounded-md">
              {orders.length} รายการ
            </span>
          </div>
        </div>
        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-100/90 border-b border-gray-200">
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ลำดับ</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">เวลาสร้างคำสั่งซื้อ</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">รหัสคำสั่งซื้อ</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">รหัสผู้ใช้</TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">เบอร์โทรศัพท์</TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">ชื่อ-นามสกุล</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">โค้ดส่วนลด</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ส่วนลด</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">จำนวน</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ราคารวม</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">สลิป</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">สถานะ</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="h-[80px]">
                  <TableCell colSpan="13">
                    <div className="w-full h-72 flex flex-col justify-center items-center gap-3 text-gray-500">
                      <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm font-semibold">กำลังโหลดข้อมูลคำสั่งซื้อ...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow className="h-[80px]">
                  <TableCell
                    colSpan="13"
                    className="h-72 font-semibold text-center text-gray-400"
                  >
                    ไม่พบข้อมูลคำสั่งซื้อที่ใช้ Promo Code
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order, index) => (
                  <OrderCell
                    order={order}
                    key={order.id}
                    index={index}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const OrderCell = ({ order, index }) => {
  const { callApi } = Context();
  const [loading, setLoading] = useState(false);

  return (
    <TableRow
      className="h-[80px] cursor-pointer hover:bg-gray-50"
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        window.open(`/orders/${order.id}`, "_blank");
      }}
    >
      <TableCell className="text-center font-semibold text-gray-500">
        {index + 1}
      </TableCell>
      <TableCell className="text-center font-medium whitespace-nowrap">
        <div>
          {new Date(order.createdAt).toLocaleDateString("th-TH", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          })}{" "}
          น.
        </div>
      </TableCell>
      <TableCell className="text-center font-semibold font-mono text-blue tabular-nums">
        {order.shortId}
      </TableCell>
      <TableCell className="text-center font-semibold text-gray-700 font-mono">
        {order.userId}
      </TableCell>
      <TableCell className="font-semibold">{order.phone}</TableCell>
      <TableCell className="font-semibold">
        {order.firstName + " " + order.lastName}
      </TableCell>
      <TableCell className="text-center font-bold">
        <span className="bg-navy/10 px-2.5 py-1 rounded-md text-xs text-blue-900 border border-navy/20">
          {order.promoCode || "-"}
        </span>
      </TableCell>
      <TableCell className="text-center font-bold text-red-600">
        {order.promoDiscount ? `-${order.promoDiscount?.toLocaleString()} บาท` : "-"}
      </TableCell>
      <TableCell className="text-center font-semibold font-mono">
        {order.size} ใบ
      </TableCell>
      <TableCell className="text-center font-bold text-gray-900">
        {order.paidCurrency === "LAK"
          ? `${order.totalPrice_lak?.toLocaleString()} (กีบ)`
          : `${order.totalPrice?.toLocaleString()} บาท`}
      </TableCell>
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        {order.slips ? (
          <img
            onClick={() => window.open(order.slips, "_blank")}
            src={order.slips}
            className="mx-auto w-16 max-h-20 object-contain cursor-pointer rounded-sm"
            alt="slip"
          />
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell className="font-semibold text-center">
        <div
          className={`${
            order.status === statuses.APPROVE
              ? "bg-green"
              : order.status === statuses.PAID
              ? "bg-blue"
              : order.status === statuses.PENDING
              ? "bg-amber-500"
              : "bg-rose-600"
          } min-w-14 w-fit px-3 py-1.5 text-white text-xs font-bold text-center rounded-md mx-auto`}
        >
          {constant.statusesTranslate[order.status] || order.status}
        </div>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center">
          <button
            title="เข้าสู่ระบบในชื่อผู้ใช้"
            className="flex items-center justify-center p-2 bg-blue text-white rounded-md shadow-xs hover:bg-blue/90 transition-all cursor-pointer disabled:opacity-50"
            onClick={async () => {
              setLoading(true);
              let result = await callApi("users-addAdminToken", {
                userId: order.userId,
              });
              if (result?.adminToken) {
                window.open(constant.getAdminOrderUrl(result.adminToken));
              }
              setLoading(false);
            }}
          >
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <BiLogIn className="w-4 h-4" />}
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};
